import {immerable} from "immer"
import Coordinates from "./common/Coordinates";
import PuzzleMatrixCell from "./PuzzleMatrixCell";

class PuzzleMatrix {
    [immerable] = true

    /**
     * @param {ResolvedPuzzleDefinition} puzzleDefinition 
     */
    constructor(puzzleDefinition) {
        /** @type {ComputedPuzzleDefinition} */
        this.definition = this._computeDefinition(puzzleDefinition);

        /** @type {PuzzleMatrixCell[][]} */
        this.rows = [];

        this._build();
    }

    shuffle() {
        for (let iRow = 0; iRow < this.definition.rows; iRow++) {
            for (let iCol = 0; iCol < this.definition.columns; iCol++) {
                const tmp = this.rows[iRow][iCol];

                const randomRow = this._randomInt(iRow, this.definition.rows);
                const minRandomCol = randomRow === iRow ? iCol : 0;
                const randomCol = this._randomInt(minRandomCol, this.definition.columns);

                this.rows[iRow][iCol] = this.rows[randomRow][randomCol];
                this.rows[randomRow][randomCol] = tmp;
            }
        }
    }

    /**
     * @param {CoordinatesObj} coordinates 
     */
    at(coordinates) {
        return this.rows[coordinates.y][coordinates.x];
    }

    /**
     * @param {CoordinatesObj} coordinates 
     * @param {PuzzleMatrixCell} cell
     */
    setAt(coordinates, cell) {
        this.rows[coordinates.y][coordinates.x] = cell;
    }

    unselectAll() {
        for (const row of this.rows)
            for (const cell of row)
                cell.selected = false;
    }

    isCompleted() {
        for (let iRow = 0; iRow < this.definition.rows; iRow++) {
            for (let iCol = 0; iCol < this.definition.columns; iCol++) {
                const cell = this.rows[iRow][iCol];
                if (!cell.areCoordinatesMatching({x: iCol, y: iRow}))
                    return false;
            }
        }
        return true;
    }

    /**
     * @param {*} containerWidth 
     * @param {*} containerHeight 
     */
    updateContainerDimensions(containerWidth, containerHeight) {
        
        this.definition.containerWidth = containerWidth;
        this.definition.containerHeight = containerHeight;

        this.definition = this._computeDefinition(this.definition);
    }

    /**
     * @param {ResolvedPuzzleDefinition} puzzleDefinition 
     */

    
    _computeDefinition(puzzleDefinition) {
       
        const imgDim = this._getImageDimensions(puzzleDefinition);

        var w = window.innerWidth;
        if(w>1010)w = 1010
        var h = w*715/1010

        if(h>window.innerHeight-80){
            h = window.innerHeight-80
            w = h * 1010 / 715
        }

        return {
            ...puzzleDefinition,
            imgWidth: w,
            imgHeight: h,
            cellWidth: w / puzzleDefinition.columns,
            cellHeight: h / puzzleDefinition.rows,
        };
    }

    /**
     * @param {ResolvedPuzzleDefinition} puzzleDefinition 
     */
    _getImageDimensions(puzzleDefinition) {
        const naturalWidth = puzzleDefinition.image?.naturalWidth || 0;
        const naturalHeight = puzzleDefinition.image?.naturalHeight || 0;

        if (puzzleDefinition.overrideWidth === undefined && puzzleDefinition.overrideHeight === undefined) {
            return {
                width: naturalWidth,
                height: naturalHeight,
            };
        }
        else if (!puzzleDefinition.isOverridePercentage && puzzleDefinition.overrideWidth !== undefined && puzzleDefinition.overrideHeight !== undefined) {
            return {
                width: puzzleDefinition.overrideWidth,
                height: puzzleDefinition.overrideHeight,
            };
        }
        else if (naturalWidth === 0)
        {
            return {
                width: 0,
                height: 0,
            };
        }
        else {
            const naturalRatio = naturalWidth / naturalHeight;

            const typeSafePuzzleOverrideWidth = puzzleDefinition.overrideWidth || 0;
            const typeSafePuzzleOverrideHeight = puzzleDefinition.overrideHeight || 0;

            const overrideWidth = puzzleDefinition.overrideWidth ?? typeSafePuzzleOverrideHeight * naturalRatio;
            const overrideHeight = puzzleDefinition.overrideHeight ?? typeSafePuzzleOverrideWidth * (1 / naturalRatio);

            if (!puzzleDefinition.isOverridePercentage) {
                return {
                    width: overrideWidth,
                    height: overrideHeight,
                };
            }

            let refWidth = puzzleDefinition.containerWidth || naturalHeight;
            let refHeight = puzzleDefinition.containerHeight || naturalHeight;

            if (refWidth > refHeight)
                refWidth = refHeight * naturalRatio;
            else
                refHeight = refWidth * (1 / naturalRatio);

            const finalWidth = refWidth * overrideWidth;
            const finalHeight = refHeight * overrideHeight;

            return {
                width: finalWidth,
                height: finalHeight,
            };
        }
    }

    _build() {
        for (let iRow = 0; iRow < this.definition.rows; iRow++) {
            /** @type {PuzzleMatrixCell[]} */
            const row = [];
            this.rows.push(row);
            
            for (let iCol = 0; iCol < this.definition.columns; iCol++) {
                row.push(new PuzzleMatrixCell({
                    imgSlice: new Coordinates({
                        x: iCol,
                        y: iRow,
                    }),
                }));
            }
        }
    }

    /**
     * @param {number} min - inclusive
     * @param {number} max - exclusive
     */
    _randomInt(min, max) {
        return min + Math.floor(Math.random() * (max - min));
    }
}

export default PuzzleMatrix;